import React, { useState, useEffect, useContext, useMemo } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { Row, Col, Form, Button, Spinner, Table, Alert, Modal } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import {
    FaArrowUp, FaArrowDown, FaChartLine, FaCalendarAlt, FaWallet,
    FaMoneyBillWave, FaShoppingCart, FaClipboardList, FaTruck,
    FaLightbulb, FaUserTie, FaEyeSlash, FaList, FaFileExport, FaBox
} from "react-icons/fa";

const ACCESS_CODE = "JPHLHR1122";
const CARD_BODY_HEIGHT = 340;

const TYPE_CONFIG = {
    sale: { label: "Sale", icon: FaShoppingCart, badgeClass: "bg-success" },
    appointment: { label: "Appointment", icon: FaClipboardList, badgeClass: "bg-info" },
    purchase: { label: "Purchase", icon: FaTruck, badgeClass: "bg-warning" },
    daily_expense: { label: "Daily Expense", icon: FaLightbulb, badgeClass: "bg-danger" },
    utility_expense: { label: "Utility Expense", icon: FaLightbulb, badgeClass: "bg-danger" },
    salary_expense: { label: "Salary Expense", icon: FaUserTie, badgeClass: "bg-danger" },
    expense: { label: "Expense", icon: FaMoneyBillWave, badgeClass: "bg-danger" }
};

const formatDateForAPI = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    return Number(amount).toFixed(2);
};

const formatDisplayDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
    });
};

const currentMonthValue = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
};

const sumAmount = (items) => items.reduce((total, item) => total + (item.amount || 0), 0);

const StatCard = ({ title, value, type, icon: Icon, onClick, subtitle }) => {
    const isPositive = type === "income" || (type === "profit" && value >= 0);
    const tone = type === "income" ? "success" : type === "expense" ? "danger" : isPositive ? "success" : "danger";
    const isClickable = typeof onClick === "function";
    const sign = type === "profit" ? (value >= 0 ? "+" : "-") : "";

    return (
        <Col xl={4} md={6}>
            <div
                className={`card h-100 border-0 shadow-sm border-start border-${tone} border-4 ${isClickable ? "fr-clickable" : ""}`}
                onClick={onClick}
            >
                <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h6 className="text-muted mb-2 small text-uppercase fw-bold fr-tracking">
                                {title}
                            </h6>
                            <h2 className={`mb-1 text-${tone} fw-bold`}>
                                {sign}{formatCurrency(Math.abs(value))}
                            </h2>
                            <small className="text-muted d-block mt-1">{subtitle}</small>
                        </div>
                        <div className={`bg-${tone} bg-opacity-10 p-3 rounded-3`}>
                            <Icon className={`text-${tone}`} size={24} />
                        </div>
                    </div>
                </div>
            </div>
        </Col>
    );
};

/**
 * One section of the report (sales, purchases, expenses, appointments).
 * The body is a fixed height so every card lines up, and scrolls on overflow.
 */
const TransactionCard = ({ title, icon: Icon, tone, items, emptyText, numberLabel, showMethod = true, renderDetails }) => (
    <Col lg={6}>
        <div className="card h-100 border-0 shadow-sm">
            <div className="card-header bg-transparent d-flex justify-content-between align-items-center py-3">
                <div className="d-flex align-items-center gap-2">
                    <span className={`bg-${tone} bg-opacity-10 text-${tone} rounded-3 d-inline-flex p-2`}>
                        <Icon size={16} />
                    </span>
                    <div>
                        <h6 className="mb-0 fw-semibold">{title}</h6>
                        <small className="text-muted">{items.length}</small>
                    </div>
                </div>
                <span className={`fw-bold text-${tone}`}>{formatCurrency(sumAmount(items))}</span>
            </div>

            <div className="fr-scroll" style={{ height: CARD_BODY_HEIGHT }}>
                {items.length === 0 ? (
                    <div className="h-100 d-flex flex-column justify-content-center align-items-center text-muted">
                        <FaWallet size={32} className="mb-2 opacity-25" />
                        <p className="mb-0 small">{emptyText}</p>
                    </div>
                ) : (
                    <Table hover size="sm" className="mb-0 align-middle">
                        <thead className="fr-sticky-head">
                            <tr>
                                <th className="ps-3">{numberLabel}</th>
                                <th>Date</th>
                                <th>Details</th>
                                {showMethod && <th>Method</th>}
                                <th className="text-end pe-3">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => (
                                <tr key={`${item.type}-${idx}`}>
                                    <td className="ps-3 text-nowrap">
                                        <span className="fw-semibold small text-capitalize">
                                            {item.reference || item.type?.replace(/_/g, " ") || "-"}
                                        </span>
                                        {item.is_refund && (
                                            <span className="badge bg-danger-subtle text-danger border border-danger-subtle ms-1">
                                                {item.exchange_type === "exchange_refund" ? "Return" : "Refund"}
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-nowrap small">{formatDisplayDate(item.date)}</td>
                                    <td>{renderDetails(item)}</td>
                                    {showMethod && (
                                        <td>
                                            <span className="badge bg-light text-dark border fw-normal">
                                                {item.payment_method || "Cash"}
                                            </span>
                                        </td>
                                    )}
                                    <td className={`text-end pe-3 fw-semibold text-nowrap ${item.is_refund ? "text-danger" : `text-${tone}`}`}>
                                        {formatCurrency(item.amount)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </div>
        </div>
    </Col>
);

const ExpenseModal = ({ show, title, items, total, onClose, onExport }) => (
    <Modal show={show} onHide={onClose} size="xl">
        <Modal.Header closeButton className="border-bottom p-2">
            <Modal.Title className="d-flex align-items-center">
                <FaList className="me-2 text-danger" size={20} />
                {title}
                <span className="ms-3 badge bg-secondary">Total: {formatCurrency(total)}</span>
            </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-3">
            {items.length === 0 ? (
                <div className="text-center py-5">
                    <FaWallet size={50} className="mb-3 text-muted opacity-25" />
                    <p className="text-muted">No expenses found for this period</p>
                </div>
            ) : (
                <div className="table-responsive">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>#</th>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Vendor / Name</th>
                                <th>Product Details</th>
                                <th>Qty</th>
                                <th>Payment Method</th>
                                <th className="text-end">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => {
                                const typeConfig = TYPE_CONFIG[item.type] || TYPE_CONFIG.expense;
                                const Icon = typeConfig.icon;
                                const isPurchase = item.type === "purchase";

                                return (
                                    <tr key={idx}>
                                        <td>{idx + 1}</td>
                                        <td>{formatDisplayDate(item.date)}</td>
                                        <td>
                                            <span className={`badge ${typeConfig.badgeClass} d-inline-flex align-items-center gap-1 px-2 py-1`}>
                                                <Icon size={12} />
                                                <span>{typeConfig.label}</span>
                                            </span>
                                        </td>
                                        <td><div className="fw-medium">{item.name || "N/A"}</div></td>
                                        <td>
                                            {isPurchase && item.product_name
                                                ? <span className="fw-medium">{item.product_name}</span>
                                                : <span className="text-muted">-</span>}
                                        </td>
                                        <td className="text-center">
                                            {isPurchase && item.qty
                                                ? <span className="badge bg-secondary">{item.qty}</span>
                                                : <span className="text-muted">-</span>}
                                        </td>
                                        <td>
                                            <span className="badge bg-light text-dark border">
                                                {isPurchase ? "-" : item.payment_method || "Cash"}
                                            </span>
                                        </td>
                                        <td className="text-end fw-bold text-danger">
                                            {formatCurrency(item.amount)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-light">
                            <tr>
                                <td colSpan="7" className="text-end fw-bold pe-3">
                                    Total Expenses (Including Purchases):
                                </td>
                                <td className="text-end fw-bold text-danger">{formatCurrency(total)}</td>
                            </tr>
                            <tr>
                                <td colSpan="7" className="text-end fw-bold pe-3 text-warning">
                                    Purchases Total:
                                </td>
                                <td className="text-end fw-bold text-warning">
                                    {formatCurrency(sumAmount(items.filter((item) => item.type === "purchase")))}
                                </td>
                            </tr>
                            <tr>
                                <td colSpan="7" className="text-end fw-bold pe-3 text-danger">
                                    Other Expenses Total:
                                </td>
                                <td className="text-end fw-bold text-danger">
                                    {formatCurrency(sumAmount(items.filter((item) => item.type !== "purchase")))}
                                </td>
                            </tr>
                        </tfoot>
                    </Table>
                </div>
            )}
        </Modal.Body>

        <Modal.Footer className="border-top">
            <Button variant="secondary" onClick={onClose}>Close</Button>
            {items.length > 0 && (
                <Button variant="primary" onClick={onExport} className="d-flex align-items-center gap-2">
                    <FaFileExport /> Export to CSV
                </Button>
            )}
        </Modal.Footer>
    </Modal>
);

export default function FinanceReport() {
    const { t } = useContext(TranslatorContext);

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState({ total_in: 0, total_out: 0, balance: 0 });
    const [reportPeriod, setReportPeriod] = useState({ start_date: "", end_date: "" });
    const [selectedMonth, setSelectedMonth] = useState(currentMonthValue);

    const [showModal, setShowModal] = useState(false);
    const [accessCode, setAccessCode] = useState("");
    const [showAlert, setShowAlert] = useState(false);

    const unlocked = accessCode === ACCESS_CODE;

    useEffect(() => {
        handleFetchForMonth(currentMonthValue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!unlocked) {
            setShowAlert(false);
            return;
        }

        setShowAlert(true);
        const timer = setTimeout(() => setShowAlert(false), 3000);
        return () => clearTimeout(timer);
    }, [unlocked]);

    const handleFetchForMonth = (monthValue) => {
        if (!monthValue) return;

        const [yearStr, monthStr] = monthValue.split("-");
        const year = parseInt(yearStr, 10);
        const month = parseInt(monthStr, 10) - 1;

        const firstDay = new Date(year, month, 1);
        firstDay.setHours(0, 0, 0, 0);

        const lastDay = new Date(year, month + 1, 0);
        lastDay.setHours(23, 59, 59, 999);

        fetchReport(firstDay, lastDay);
    };

    const fetchReport = async (start, end) => {
        setLoading(true);

        const startDate = formatDateForAPI(start);
        const endDate = formatDateForAPI(end);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/finance-report`,
                {
                    params: { start_date: startDate, end_date: endDate },
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data?.status) {
                setReportData(response.data.data || []);
                setSummary(response.data.summary || { total_in: 0, total_out: 0, balance: 0 });
                setReportPeriod({ start_date: startDate, end_date: endDate });
            }
        } catch (error) {
            console.error("Error fetching report:", error);
            alert(t("failed_to_load_report"));
        } finally {
            setLoading(false);
        }
    };

    // Four sections of the report. Anything debit that is not a purchase is an expense.
    const sections = useMemo(() => {
        const grouped = { sale: [], purchase: [], expense: [], appointment: [] };

        reportData.forEach((item) => {
            if (item.type === "sale") grouped.sale.push(item);
            else if (item.type === "purchase") grouped.purchase.push(item);
            else if (item.type === "appointment") grouped.appointment.push(item);
            else if (item.transaction_type === "debit") grouped.expense.push(item);
        });

        return grouped;
    }, [reportData]);

    const allExpenses = useMemo(
        () => reportData.filter((item) => item.transaction_type === "debit"),
        [reportData]
    );

    const handleExportCSV = () => {
        if (allExpenses.length === 0) return;

        const headers = ["Date", "Type", "Vendor/Name", "Product Name", "Quantity", "Payment Method", "Amount"];
        const rows = allExpenses.map((item) => [
            formatDisplayDate(item.date),
            item.type,
            item.name || "",
            item.product_name || "",
            item.qty || "",
            item.payment_method || "-",
            item.amount
        ].join(","));

        const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `expenses_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    const nameDetails = (item) => (
        <div className="fw-medium text-truncate" style={{ maxWidth: 220 }}>{item.name}</div>
    );

    const purchaseDetails = (item) => (
        <div>
            <div className="fw-medium text-truncate" style={{ maxWidth: 200 }}>{item.name}</div>
            {item.product_name && (
                <small className="text-muted d-block">
                    <FaBox className="me-1" size={10} />
                    {item.product_name}{item.qty ? ` (x${item.qty})` : ""}
                </small>
            )}
            {item.sale_number && (
                <small className="text-muted">{t("Sold on")} {item.sale_number}</small>
            )}
        </div>
    );

    const expenseDetails = (item) => (
        <div className="fw-medium text-truncate" style={{ maxWidth: 200 }}>{item.name}</div>
    );

    return (
        <PageLayout>
            <div className="container-fluid py-3">
                {showAlert && (
                    <Alert
                        variant="success"
                        className="position-fixed top-0 end-0 m-3 shadow"
                        style={{ zIndex: 1050 }}
                        onClose={() => setShowAlert(false)}
                        dismissible
                    >
                        {t("Access Granted! Financial summary unlocked.")}
                    </Alert>
                )}

                <div className="mb-4">
                    <h2 className="mb-1 fw-bold">
                        <FaChartLine className="me-2 text-primary" />
                        {t("Finance Report")}
                    </h2>
                    <small className="text-muted">
                        {reportPeriod.start_date && reportPeriod.end_date && (
                            <>Period: {formatDisplayDate(reportPeriod.start_date)} - {formatDisplayDate(reportPeriod.end_date)}</>
                        )}
                    </small>
                </div>

                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-4">
                                <Form.Label className="fw-semibold small text-muted mb-1 d-block">
                                    <FaCalendarAlt className="me-1" size={12} />
                                    {t("MONTH")}
                                </Form.Label>
                                <input
                                    type="month"
                                    className="form-control"
                                    value={selectedMonth}
                                    onChange={(e) => {
                                        setSelectedMonth(e.target.value);
                                        handleFetchForMonth(e.target.value);
                                    }}
                                />
                            </div>
                            <div className="col-md-5">
                                <Form.Label className="fw-semibold small text-muted mb-1 d-block">
                                    {t("ACCESS CODE")}
                                </Form.Label>
                                <input
                                    type="password"
                                    className="form-control"
                                    placeholder={t("Enter access code")}
                                    value={accessCode}
                                    onChange={(e) => setAccessCode(e.target.value)}
                                />
                            </div>
                            {unlocked && (
                                <div className="col-md-3">
                                    <Button
                                        variant="secondary"
                                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                                        onClick={() => setAccessCode("")}
                                    >
                                        <FaEyeSlash /> {t("Hide Data")}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {!unlocked ? (
                    <div className="text-center py-5 text-muted border rounded bg-light shadow-sm">
                        <FaEyeSlash size={40} className="mb-3 opacity-25" />
                        <p className="mb-0 fw-medium">
                            {t("Please enter a valid access code to unlock sensitive financial matrices.")}
                        </p>
                    </div>
                ) : (
                    <>
                        <Row className="g-3 mb-4">
                            <StatCard
                                title={t("Total Income")}
                                value={summary.total_in}
                                type="income"
                                icon={FaArrowDown}
                                subtitle={t("All revenue sources")}
                            />
                            <StatCard
                                title={t("Total Expenses")}
                                value={summary.total_out}
                                type="expense"
                                icon={FaArrowUp}
                                onClick={() => setShowModal(true)}
                                subtitle={t("Click to view all expenses")}
                            />
                            <StatCard
                                title={t("Net Profit / Loss")}
                                value={summary.balance}
                                type="profit"
                                icon={summary.balance >= 0 ? FaArrowDown : FaArrowUp}
                                subtitle={t("After all expenses")}
                            />
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">{t("Loading...")}</p>
                            </div>
                        ) : (
                            <Row className="g-3">
                                <TransactionCard
                                    title={t("Sales")}
                                    icon={FaShoppingCart}
                                    tone="success"
                                    items={sections.sale}
                                    numberLabel={t("Sale #")}
                                    emptyText={t("No sales in this period")}
                                    renderDetails={nameDetails}
                                />
                                <TransactionCard
                                    title={t("Purchases")}
                                    icon={FaTruck}
                                    tone="warning"
                                    items={sections.purchase}
                                    numberLabel={t("Purchase #")}
                                    emptyText={t("No purchases in this period")}
                                    showMethod={false}
                                    renderDetails={purchaseDetails}
                                />
                                <TransactionCard
                                    title={t("Expenses")}
                                    icon={FaMoneyBillWave}
                                    tone="danger"
                                    items={sections.expense}
                                    numberLabel={t("Type")}
                                    emptyText={t("No expenses in this period")}
                                    renderDetails={expenseDetails}
                                />
                                <TransactionCard
                                    title={t("Appointments")}
                                    icon={FaClipboardList}
                                    tone="info"
                                    items={sections.appointment}
                                    numberLabel={t("Appointment #")}
                                    emptyText={t("No appointments in this period")}
                                    renderDetails={nameDetails}
                                />
                            </Row>
                        )}
                    </>
                )}

                <ExpenseModal
                    show={showModal}
                    title="All Expenses (Including Purchases)"
                    items={allExpenses}
                    total={sumAmount(allExpenses)}
                    onClose={() => setShowModal(false)}
                    onExport={handleExportCSV}
                />
            </div>

            <style>{`
                .fr-clickable {
                    cursor: pointer;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .fr-clickable:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
                }
                .fr-tracking {
                    letter-spacing: 0.05em;
                }
                .fr-scroll {
                    overflow-y: auto;
                }
                .fr-sticky-head th {
                    position: sticky;
                    top: 0;
                    z-index: 2;
                    background: var(--bs-body-bg, #fff);
                    box-shadow: inset 0 -1px 0 var(--bs-border-color, #dee2e6);
                }
            `}</style>
        </PageLayout>
    );
}
